import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

export type AdminStateVariant =
  | 'empty-no-data'
  | 'empty-no-results'
  | 'error-retry'
  | 'network-error'
  | 'permission-denied'
  | 'not-found';

interface AdminStateCopy {
  heading: string;
  message: string;
}

const COPY: Record<AdminStateVariant, AdminStateCopy> = {
  'empty-no-data': {
    heading: 'Nothing here yet',
    message: 'There is no data to show yet.',
  },
  'empty-no-results': {
    heading: 'No results',
    message: 'No results match your filters.',
  },
  'error-retry': {
    heading: 'Something went wrong',
    message: 'We could not load this — try again.',
  },
  'network-error': {
    heading: 'Could not connect',
    message: 'Check your connection and try again.',
  },
  'permission-denied': {
    heading: 'Access denied',
    message: 'You do not have permission to view this.',
  },
  'not-found': {
    heading: 'Not found',
    message: 'We could not find what you were looking for.',
  },
};

@Component({
  selector: 'admin-state',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './state.component.html',
  styleUrl: './state.component.scss',
  host: { role: 'status' },
})
export class AdminStateComponent {
  @Input({ required: true }) variant!: AdminStateVariant;
  @Input() heading: string | null = null;
  @Input() message: string | null = null;
  @Input() filterSummary: string | null = null;
  @Input() actionLabel: string | null = null;
  @Output() action = new EventEmitter<void>();

  get copy(): AdminStateCopy {
    return COPY[this.variant];
  }

  get headingText(): string {
    return this.heading ?? this.copy.heading;
  }

  get messageText(): string {
    if (this.message) {
      return this.message;
    }

    return this.variant === 'empty-no-results' && this.filterSummary
      ? this.filterSummary
      : this.copy.message;
  }

  onAction(): void {
    this.action.emit();
  }
}
